import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ViewChild, computed, inject, signal } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, LoaderComponent, MbsSize, ModalComponent, ModalFooterDirective, ModalService } from 'mbs-ui-kit';
import { GetPurchasePayload } from 'mbs-ui-shared@components/buy-license-modal/models/calculator';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LicensesSignalFacade } from 'mbs-ui-shared@facades/licenses-signal.facade';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { LicenseType } from 'mbs-ui-shared@models';
import { SubscriptionLicenseMode } from 'mbs-ui-shared@models/License';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { Observable, catchError, finalize, map, of, switchMap, take } from 'rxjs';
import { PremiumSupportActivatedModalComponent } from '../premium-support-activated-modal/premium-support-activated-modal.component';

type PremiumSupportFeature = {
  id: 'support24x7' | 'onlineChat' | 'priorityService';
  iconClass: string;
  label: string;
  cardClass: string;
};

@Component({
  selector: 'mbs-buy-premium-support-modal',
  standalone: true,
  templateUrl: './buy-premium-support-modal.component.html',
  styleUrls: ['./buy-premium-support-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, I18NextPipe, ButtonComponent, LoaderComponent, ModalComponent, ModalFooterDirective]
})
export class BuyPremiumSupportModalComponent implements AfterViewInit {
  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  private licensesFacade = inject(LicensesFacade);
  private licensesService = inject(LicensesService);
  private licensesSignalFacade = inject(LicensesSignalFacade);
  private authFacade = inject(AuthFacade);
  private modalService = inject(ModalService);

  public readonly loading = signal(false);

  public readonly isPostPayment = this.licensesSignalFacade.isPostPayment;

  public readonly submitLabelKey = computed(() =>
    this.isPostPayment() ? 'shared:licenses.buyPremiumSupportModal.activate' : 'shared:licenses.buyPremiumSupportModal.buy'
  );

  public readonly pricePerLicense = signal(0);
  public readonly billingPeriod = signal<'month' | 'year'>('year');
  public readonly periodLabelKey = computed(() =>
    this.billingPeriod() === 'year' ? 'shared:licenses.buyPremiumSupportModal.perYear' : 'shared:licenses.buyPremiumSupportModal.perMonth'
  );
  public readonly pricesLoading = signal(true);

  private readonly isFirstPurchase = signal(false);

  public readonly features: PremiumSupportFeature[] = [
    {
      id: 'support24x7',
      iconClass: 'fa fa-thumbs-up',
      label: 'shared:licenses.buyPremiumSupportModal.feature24x7',
      cardClass: 'premium-card--indigo'
    },
    {
      id: 'onlineChat',
      iconClass: 'fa fa-comments',
      label: 'shared:licenses.buyPremiumSupportModal.featureOnlineChat',
      cardClass: 'premium-card--yellow'
    },
    {
      id: 'priorityService',
      iconClass: 'fa fa-star',
      label: 'shared:licenses.buyPremiumSupportModal.featurePriorityService',
      cardClass: 'premium-card--teal'
    }
  ];

  ngAfterViewInit(): void {
    this.loadPrice();
  }

  private loadPrice(): void {
    const isMonthly = this.licensesSignalFacade.isMonthlyLicenseMode();
    this.billingPeriod.set(isMonthly ? 'month' : 'year');

    this.pricesLoading.set(true);
    this.licensesFacade
      .getPrices([LicenseType.PremiumSupport])
      .pipe(
        take(1),
        catchError(() => of(null)),
        finalize(() => this.pricesLoading.set(false))
      )
      .subscribe((data) => {
        this.isFirstPurchase.set(!!data?.isFirstPurchase);
        const priceRow = data?.licenseTypesPricesMap?.[LicenseType.PremiumSupport];
        const prices = isMonthly ? priceRow?.monthlyPrices : priceRow?.annualPrices;
        const unit = prices?.unitDiscountedPrice ?? prices?.unitListPrice ?? 0;
        this.pricePerLicense.set(unit);
      });
  }

  private getNewLicenseMode(): SubscriptionLicenseMode | null {
    if (this.isPostPayment()) return SubscriptionLicenseMode.PostPaymentV1Monthly;

    const version = this.licensesSignalFacade.prePaymentLicenseModeVersion();
    const licenseModes = {
      1: [SubscriptionLicenseMode.Monthly, SubscriptionLicenseMode.Yearly],
      2: [SubscriptionLicenseMode.CumulativeV2Monthly, SubscriptionLicenseMode.CumulativeV2Annual],
      3: [SubscriptionLicenseMode.CumulativeV3Monthly, SubscriptionLicenseMode.CumulativeV3Annual],
      4: [SubscriptionLicenseMode.CumulativeV4Monthly, SubscriptionLicenseMode.CumulativeV4Annual]
    };

    return licenseModes[version]?.[this.billingPeriod() === 'month' ? 0 : 1] ?? null;
  }

  public submit(): void {
    this.loading.set(true);

    const request$ = this.isPostPayment() ? this.assignLicense() : this.purchaseLicense();

    request$
      .pipe(
        take(1),
        finalize(() => this.loading.set(false))
      )
      .subscribe((activated) => {
        if (!activated) return;

        this.baseModal.close();
        this.modalService.openCustom(PremiumSupportActivatedModalComponent, { size: MbsSize.sm });
      });
  }

  // Post payment has nothing to pay for: the license is assigned right away and lands in the next invoice
  private assignLicense(): Observable<boolean> {
    return this.licensesService.licenseAssign({ licenseTypeId: LicenseType.PremiumSupport }).pipe(
      switchMap(() => this.refreshCurrentUser()),
      catchError(() => of(false))
    );
  }

  private purchaseLicense(): Observable<boolean> {
    const newLicenseMode = this.isFirstPurchase() ? this.getNewLicenseMode() : null;

    const payload = {
      licenseTypesCounts: [{ type: LicenseType.PremiumSupport, count: 1, existCount: 0 }],
      ...(newLicenseMode !== null && { newLicenseMode })
    } as unknown as GetPurchasePayload;

    return this.licensesFacade.getPurchase(payload).pipe(
      switchMap((result) => {
        if (result?.link) {
          location.href = result.link.replace(/^~\//, '/');

          return of(false);
        }

        return this.refreshCurrentUser();
      }),
      catchError(() => of(false))
    );
  }

  private refreshCurrentUser(): Observable<boolean> {
    return this.authFacade.fetchCurrentUser$.pipe(
      catchError(() => of(null)),
      map(() => true)
    );
  }

  public cancel(): void {
    this.baseModal.close();
  }
}
